import { defineStore } from "pinia";
import axios from "axios";
import SecureLS from "secure-ls";

const ls = new SecureLS();

export const useAuthStore = defineStore("authStore", {
  state: () => {
    let user = null;
    try {
      const userData = ls.get("user") ?? null;
      user = userData;
    } catch (e) {
      console.error("Failed to get user from secure storage:", e);
    }

    return {
      token: ls.get("token") || null,
      user: user,
      permissions: ls.get("permissions") || [],
    };
  },

  actions: {
    async login(token, user) {
      this.token = token;
      this.user = user;
      ls.set("token", token);
      ls.set("user", user);
    },

    setToken(token) {
      this.token = token;
      ls.set("token", token);
    },

    setUser(user) {
      this.user = user;
      ls.set("user", user);
    },

    setPermissions(permissions) {
      this.permissions = permissions;
      ls.set("permissions", permissions);
    },

    logout() {
      this.token = null;
      this.user = null;
      this.permissions = [];
      ls.remove("token");
      ls.remove("user");
      ls.remove("permissions");

      // Ensure navigation is handled from a Vue router context
      if (typeof this.$router?.push === "function") {
        this.$router.push({ name: "login" });
      } else {
        window.location.href = "/login";
      }
    },

    async fetchResource(endpoint) {
      try {
        const response = await axios.get(
          `${window.getRuntimeConfig("VUE_APP_API_URL", process.env.VUE_APP_API_URL)}${endpoint}`,
          {
            headers: {
              Authorization: `Bearer ${this.token}`,
            },
          }
        );
        return response;
      } catch (error) {
        console.error("API fetch failed:", error);
        throw error;
      }
    },

    async syncPermissions() {
      try {
        const res = await this.fetchResource("user/permissions");
        this.setPermissions(res.data.data);
        return res.data.data;
      } catch (e) {
        this.setPermissions([]);
        return [];
      }
    },
  },

  getters: {
    getToken: (state) => state.token ?? "",
    getUser: (state) => state.user ?? {},
    getRole: (state) => state.user?.role ?? "",
    getPermissions: (state) => state.permissions ?? [],
    hasPermission: (state) => (permission) => {
      if (permission === "any") return true;
      if (Array.isArray(permission)) {
        return permission.some((p) => state.permissions.includes(p));
      }
      return state.permissions.includes(permission);
    },
  },

  persist: {
    enabled: false, // optional: Pinia-plugin-persistedstate can be used instead
  },
});
